/**
 * Google Identity Services (login com Google — Fase 8).
 *
 * Carrega sob demanda o script oficial `https://accounts.google.com/gsi/client` (uma vez; a promise
 * é compartilhada) e expõe os tipos mínimos de `google.accounts.id` que o app usa: `initialize`,
 * `renderButton`, `prompt` e `disableAutoSelect`. O botão em si é desenhado pelo Google dentro de um
 * container nosso (Login.tsx); o callback recebe o **ID token** (`credential`), que o backend valida
 * em `POST /auth/exchange { provider: 'google', idToken }`.
 */

const GSI_SRC = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
  /** ID token (JWT) assinado pelo Google. */
  credential: string;
  select_by?: string;
  clientId?: string;
}

export interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: 'signin' | 'signup' | 'use';
  ux_mode?: 'popup' | 'redirect';
  itp_support?: boolean;
  use_fedcm_for_prompt?: boolean;
}

export interface GsiButtonConfiguration {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  /** Largura em px (máx. 400). */
  width?: number;
  locale?: string;
}

export interface GoogleAccountsId {
  initialize: (config: GoogleIdConfiguration) => void;
  renderButton: (parent: HTMLElement, options: GsiButtonConfiguration) => void;
  prompt: (listener?: (notification: unknown) => void) => void;
  disableAutoSelect: () => void;
  cancel?: () => void;
}

interface GoogleGlobal {
  accounts?: { id?: GoogleAccountsId };
}

declare global {
  interface Window {
    google?: GoogleGlobal;
  }
}

let loadPromise: Promise<GoogleAccountsId> | null = null;

/** `google.accounts.id` se o script já estiver carregado (sem carregar). */
export const getGoogleIdentity = (): GoogleAccountsId | null =>
  (typeof window !== 'undefined' && window.google?.accounts?.id) || null;

/** Carrega o script do GIS (uma vez) e devolve `google.accounts.id`. */
export const loadGoogleIdentity = (): Promise<GoogleAccountsId> => {
  const ready = getGoogleIdentity();
  if (ready) return Promise.resolve(ready);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<GoogleAccountsId>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Google Identity Services só funciona no navegador'));
      return;
    }
    const finish = () => {
      const api = getGoogleIdentity();
      if (api) resolve(api);
      else reject(new Error('Google Identity Services carregou sem expor google.accounts.id'));
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('Falha ao carregar o script do Google')), { once: true });
      // Pode já ter carregado antes de registrarmos o listener.
      if (getGoogleIdentity()) finish();
      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Falha ao carregar o script do Google'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};
